import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { listAdminPaymentInstructions, upsertPaymentInstruction } from "@/lib/payments-admin.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () => queryOptions({ queryKey: ["admin", "payment_instructions"], queryFn: () => listAdminPaymentInstructions() });

export const Route = createFileRoute("/_authenticated/admin/payments")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: PaymentsAdmin,
});

function PaymentsAdmin() {
  const { data } = useSuspenseQuery(listQ());
  return (
    <div>
      <header className="mb-6">
        <p className="kicker text-modern-orange">إدارة المحتوى</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">تعليمات الدفع</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          حرّر النصوص التي تظهر للعميلة في صفحة تأكيد الحجز.
        </p>
        <p className="mt-2 text-sm font-semibold text-foreground">
          كل عنوان ### يصير بطاقة مستقلة لطريقة دفع، وسطر --- يفصل الملاحظات الختامية.
        </p>
      </header>

      <div className="mb-6 rounded-ds-lg border border-border bg-card p-5 shadow-soft">
        <h2 className="font-display text-lg font-extrabold text-foreground">أين تظهر هذه التعليمات؟</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <InstructionHint title="inside_syria" body="تظهر للحجوزات من داخل سوريا. خيار الدفع الوحيد للمستخدمين هناك هو تحويل داخلي." />
          <InstructionHint title="outside_syria" body="تظهر للحجوزات من خارج سوريا حسب طريقة الدفع التي تختارها المستخدمة." />
          <InstructionHint title="bousala_inside_syria" body="تظهر في صفحة شراء بوصلة د. سارة للمستخدمين داخل سوريا. مستقلة عن تعليمات جلسات الاستشارة." />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {data.map((p: any) => (
          <Editor key={p.id} initial={p} />
        ))}
      </div>
    </div>
  );
}

function InstructionHint({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-background/70 p-4">
      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-foreground" aria-hidden />
      <div>
        <p className="font-display text-sm font-bold text-foreground">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function Editor({ initial }: { initial: any }) {
  const [form, setForm] = useState({
    id: initial.id as string,
    region_slug: initial.region_slug as string,
    title_ar: (initial.title_ar as string) ?? "",
    body_md: (initial.body_md as string) ?? "",
    is_active: !!initial.is_active,
    sort_order: Number(initial.sort_order ?? 0),
  });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = useServerFn(upsertPaymentInstruction);

  async function submit() {
    setBusy(true);
    try {
      await save({ data: form });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "payment_instructions"] });
      qc.invalidateQueries({ queryKey: ["public", "payment_instructions"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الحفظ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ds-lg bg-card p-6 shadow-soft">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-extrabold text-foreground">{form.title_ar}</h2>
        <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
          {form.region_slug}
        </span>
      </div>
      <label className="block text-sm">
        <span className="mb-1 block font-semibold text-foreground">العنوان</span>
        <input
          value={form.title_ar}
          onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
          className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
        />
      </label>
      <label className="mt-4 block text-sm">
        <span className="mb-1 block font-semibold text-foreground">النص (Markdown مدعوم)</span>
        <textarea
          value={form.body_md}
          onChange={(e) => setForm({ ...form, body_md: e.target.value })}
          rows={10}
          className="w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono text-xs outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          dir="rtl"
        />
      </label>
      {["paypal_outside_syria", "outside_syria"].includes(form.region_slug) ? (
        <p className="mt-3 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
          هذا النص لا يظهر للعميل حالياً — زر PayPal في الصفحة يكفي.
        </p>
      ) : null}
      <div className="mt-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          <span>نشطة</span>
        </label>
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ التعليمات
        </button>
      </div>
    </section>
  );
}